import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormSelect } from '../../ui/Select'
import { Button } from '../../ui'

const getFilterErrors = (errors, filterId) => {
  if (!errors || !filterId)
    return null

  return errors[`summary_filters[${filterId}]`] || null
}

const SummaryVariableFilters = ({
  activeFilters, errors, variableOptions, valueOptions, collect,
  onAddFilter, onRemoveFilter, onVariableChange, onValuesChange
}) => {
  const handleVariableChange = useCallback((uniqueId, selected) => {
    onVariableChange(uniqueId, selected?.value)
  }, [onVariableChange])

  const handleValuesChange = useCallback((uniqueId, selected) => {
    onValuesChange(uniqueId, selected?.map(option => option.value) || [])
  }, [onValuesChange])

  const handleVariableChangeForFilter = useCallback(uniqueId =>
    selected => handleVariableChange(uniqueId, selected), [handleVariableChange])

  const handleValuesChangeForFilter = useCallback(uniqueId =>
    selected => handleValuesChange(uniqueId, selected), [handleValuesChange])

  const handleRemoveFilter = useCallback(uniqueId =>
    () => onRemoveFilter(uniqueId), [onRemoveFilter])

  const noOptionsMessage = useCallback(variableId =>
    () => {
      if (variableId)
        return 'Aucune option disponible'

      return 'Veuillez d\'abord sélectionner une variable'
    }, [])

  return (
    <>
      {activeFilters.map((filter, index) => {
        const filterErrors = getFilterErrors(errors, filter.id, index)
        return (
          <div key={filter.uniqueId}>
            <hr />
            <span className="filter-number">
              {`Filtre N°${index + 1}`}
            </span>
            <button
              type="button"
              className="btn btn-link text-danger p-0 ml-1"
              onClick={handleRemoveFilter(filter.uniqueId)}
            >
              <i className="fas fa-trash" />
            </button>
            <div className="ml-2 mt-2">
              <label>Sélectionner une variable</label>
              <FormSelect
                className="mb-2"
                options={variableOptions}
                value={filter.variableId ? {
                  value: filter.variableId,
                  label: collect.variables?.find(
                    variable => variable.id.toString() === filter.variableId.toString()
                  )?.name
                } : null}
                placeholder="Sélectionner une variable"
                errorMessage={filterErrors?.variable?.[0] || ''}
                onChange={handleVariableChangeForFilter(filter.uniqueId)}
              />
              <label>Sélectionner une ou plusieurs valeurs</label>
              <FormSelect
                closeMenuOnSelect={false}
                errorMessage={filterErrors?.values?.[0] || ''}
                isDisabled={!filter.variableId}
                noOptionsMessage={noOptionsMessage(filter.variableId)}
                options={valueOptions(filter.variableId)}
                placeholder="Sélectionner des valeurs"
                value={filter.values?.map(value => {
                  const options = valueOptions(filter.variableId)
                  const option = options.find(opt => opt.value === value)
                  return option || { value, label: value }
                })}
                isClearable
                isMulti
                showSelectAll
                onChange={handleValuesChangeForFilter(filter.uniqueId)}
              />
            </div>
          </div>
        )
      })}
      <div className="d-flex flex-column flex-sm-row mt-3">
        <Button
          variant="outline"
          icon="plus"
          onClick={onAddFilter}
        >
          Ajouter un filtre
        </Button>
      </div>
    </>
  )
}

SummaryVariableFilters.defaultProps = { errors: null }

SummaryVariableFilters.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.shape({
    uniqueId: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.string),
    variableId: PropTypes.oneOfType([
      PropTypes.number, PropTypes.string
    ])
  })).isRequired,
  collect: PropTypes.shape({
    variables: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  }).isRequired,
  errors: PropTypes.objectOf(PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.string)
  )),
  onAddFilter: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  onVariableChange: PropTypes.func.isRequired,
  valueOptions: PropTypes.func.isRequired,
  variableOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string, PropTypes.number
    ])
  })).isRequired
}

export default SummaryVariableFilters
